import { Interface } from '@/shared/di/interface'
import { IAssets } from '@/shared/di/interface/assets'
import { container } from 'tsyringe'
import BaseController from './base-controller'
import {
  Color,
  Euler,
  FrontSide,
  MathUtils,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector2,
  Vector3
} from 'three'
import physicsFactory from './physics-factory'
import { Body } from 'matter-js'
import gsap, { Sine } from 'gsap'
import {
  BODY_LABEL_ADDON_FROM_SLIT,
  GLOBAL_COIN_SCALE,
  MATTER_TO_THREE_SCALE,
  MODEL_IMPORT_SCALE,
  PhysicsLayerZ,
  SpawnLocation
} from './misc/constants'
import { Asset } from '@/state/vault/domain'
import { Id } from '@/common/base-types'
import TingDefinition from './configs/ting-definition'
import { ISound } from '@/shared/di/interface/sound'
import { Howl } from 'howler'
import { CollisionGroup } from './rooms/room-physics-base'
import { ISize } from '@/shared/di/interface/size'
import globalRenderer from '@/shared/misc/global-renderer'

export default class TingController extends BaseController {
  fromWalletId = ''
  assetId: Id = ''
  asset: Asset = {} as Asset
  isZoomed = false
  useIdleRotation = false
  spinSpeed = 0

  spawnLocation: SpawnLocation

  #firstUpdate = true
  #definition: TingDefinition = {} as TingDefinition
  #animatedRotation = new Vector3()
  #randomFactor = Math.random()
  #zoomInPosition = new Vector3()
  #rotateVector = new Vector2()
  #spinQuaternion = new Quaternion()
  #savedQuaterinion = new Quaternion()
  #spinSound: Howl | undefined
  #zoomInTween: gsap.core.Tween | undefined

  constructor(asset: Asset, spawnLocation: SpawnLocation) {
    super()

    this.asset = asset
    this.assetId = asset.id
    this.isLoaded = false
    this.spawnLocation = spawnLocation

    //this.initBody()

    this.isLoaded = true
  }

  load(): Promise<void> {
    return new Promise(resolve => {
      const assets = container.resolve<IAssets>(Interface.IAssets)
      //get definitions based in asset series
      const definitionsFile = 'series/' + this.asset.t + '/definitions.json'

      assets.loadDefinitions(definitionsFile).then((defs: TingDefinition[]) => {
        const foundDefinition = defs.find(
          item =>
            item.currency.toLowerCase() === this.asset.c.toLowerCase() &&
            item.variant.toLowerCase() === String(this.asset.va).toLowerCase()
        )
        if (foundDefinition) {
          this.#definition = foundDefinition
        } else {
          throw new Error('no coin definition found')
        }

        assets.loadSingleTing(this.#definition).then(() => {
          const model = assets.getModel(this.#definition.modelName) as Object3D
          if (model) {
            this.processModel(model)
            this.isLoaded = true
          }
          resolve()
        })
      })
    })
  }

  processModel(model: Object3D) {
    const tingMesh = model.clone().children[0] as Mesh

    //const geometry = tingMesh.geometry
    //geometry.deleteAttribute('tangents')
    //delete geometry.attributes.tangent
    //geometry.computeTangents()

    this.mesh = tingMesh
    this.meshInitScale = MODEL_IMPORT_SCALE * GLOBAL_COIN_SCALE * 0.95
    tingMesh.scale.setScalar(this.meshInitScale)

    tingMesh.rotation.x = Math.PI * 0.5
    tingMesh.castShadow = true
    tingMesh.receiveShadow = true
    tingMesh.userData.enableBloom = globalRenderer.allowBloomOnTokens

    if (this.asset.va?.toLowerCase() === 'majblommacoin') {
      this.body = physicsFactory.createCircle(180)
      tingMesh.userData.enableBloom = false
    } else if (this.asset.va?.toLowerCase() === 'majblomma') {
      this.body = physicsFactory.createCircle(100)
      tingMesh.children.forEach(c => {
        c.userData.enableBloom = false
        if ((c as Mesh).name === 'needle') {
          ;((c as Mesh).material as MeshStandardMaterial).color.set(0x999999)
          ;((c as Mesh).material as MeshStandardMaterial).metalness = 1
        }
      })
    } else if (this.asset.va?.toLowerCase() === 'red') {
      this.body = physicsFactory.createCircle(95)
      tingMesh.material = new MeshStandardMaterial({
        side: FrontSide,
        color: this.baseColor,
        envMapIntensity: 1,
        roughness: 0,
        metalness: 1,
        transparent: false
      })
    } else if (this.asset.va?.toLowerCase() === 'gold') {
      this.body = physicsFactory.createCircle(95)
      tingMesh.material = new MeshStandardMaterial({
        color: 16764263,
        envMapIntensity: 1,
        roughness: 0,
        metalness: 1,
        transparent: false,
        flatShading: false
      })

      tingMesh.material.userData.shader = {
        uniforms: {
          isBloomRender: { value: 0 }
        }
      }

      tingMesh.material.onBeforeCompile = shader => {
        shader.uniforms.isBloomRender = { value: 0 }

        shader.fragmentShader = shader.fragmentShader
          .replace(
            'uniform vec3 diffuse;',
            `
            uniform vec3 diffuse;
            uniform float isBloomRender;
          `
          )
          .replace(
            '#include <output_fragment>',
            `
            #include <output_fragment>
            
            gl_FragColor.rgb = mix(gl_FragColor.rgb, clamp(totalSpecular,0.0,0.8)*0.5, isBloomRender);
  
          `
          )
        ;(tingMesh.material as MeshStandardMaterial).userData.shader = shader
      }
    }

    this.initBody()
  }

  onTouchesChanged = (touches: Touch[]) => {
    if (!this.isZoomed || touches.length === 0) {
      this.lastTouchesLength = 0
      return
    }

    //if new, set start position, to get correct delta
    if (this.lastTouchesLength === 0 && touches.length > 0) {
      this.previousPointerPosition.x = touches[0].clientX
      this.previousPointerPosition.y = touches[0].clientY
    }

    this.#rotateVector.set(
      touches[0].clientX - this.previousPointerPosition.x,
      touches[0].clientY - this.previousPointerPosition.y
    )

    this.previousPointerPosition.set(touches[0].clientX, touches[0].clientY)
    this.lastTouchesLength = touches.length
  }

  initBody() {
    this.body.label = 'ting'
    Body.setDensity(this.body, this.#definition.weight)
    super.initBody()

    this.body.plugin.rotateConstraintX = -20
  }

  getBody() {
    return this.body
  }

  get baseColor() {
    return this.#definition.baseColor
  }

  update(time: number, delta: number) {
    if (!this.isLoaded) {
      return
    }

    if (this.isAnimated) {
      return
    }

    if (this.isZoomed) {
      this.zoomUpdate(time, delta)
      return
    } else {
      super.update(time, delta)
    }

    const toAngle = -this.body.angle + this.initAngle

    let animRotX = Math.sin(time * 0.06 * Math.PI * 2 - this.#randomFactor * 30) * 0.3
    let animRotZ = Math.sin(time * 0.06 * Math.PI * 2 + this.#randomFactor * 30) * 0.3

    if (!this.useIdleRotation) {
      animRotX *= 0.3
      animRotZ *= 0.3
    }

    this.#animatedRotation.set(animRotX + Math.PI * 0.5, toAngle, animRotZ)

    const fromSlitState = this.body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)

    if (this.body.collisionFilter.group === CollisionGroup.ActiveCoins || fromSlitState) {
      this.mesh.rotation.y = toAngle
      this.mesh.rotation.x += (Math.PI * 0.5 - this.mesh.rotation.x) * 0.3

      if (!fromSlitState) {
        const velZ = this.mesh.position.z - this.lastPosition.z
        let pickUpAngle = 0
        const size = container.resolve<ISize>(Interface.ISize).getSize()

        if (this.body.position.x < size.width * -0.47) {
          pickUpAngle = velZ * Math.PI
        } else if (this.body.position.x > size.width * 0.47) {
          pickUpAngle = -velZ * Math.PI
        }

        this.mesh.rotation.z += (pickUpAngle - this.mesh.rotation.z) * 0.1
      }
    } else {
      this.mesh.rotation.y = toAngle
      this.mesh.rotation.x += (this.#animatedRotation.x - this.mesh.rotation.x) * (this.#firstUpdate ? 1 : 0.3)
      this.mesh.rotation.z += (this.#animatedRotation.z - this.mesh.rotation.z) * (this.#firstUpdate ? 1 : 0.3)
    }

    this.#firstUpdate = false
  }

  zoomUpdate(time: number, delta: number) {
    const deltaRotationQuaternion = new Quaternion().setFromEuler(
      new Euler(MathUtils.degToRad(this.#rotateVector.y * 1), MathUtils.degToRad(this.#rotateVector.x * 1), 0, 'XYZ')
    )

    this.#rotateVector.multiplyScalar(0.97)

    this.#spinQuaternion.multiplyQuaternions(deltaRotationQuaternion, this.mesh.quaternion)
    this.mesh.position.copy(this.#zoomInPosition)

    this.mesh.quaternion.slerp(this.#spinQuaternion, 0.7)
    this.spinSpeed = this.mesh.quaternion.angleTo(this.#spinQuaternion)

    if (this.#spinSound) {
      const sound = container.resolve<ISound>(Interface.ISound)
      sound.setLoopVolume('drag_2', Math.min(1, this.spinSpeed * 10))
    }
  }

  zoomIn(position: Vector3): Promise<void> {
    const sound = container.resolve<ISound>(Interface.ISound)
    if (this.isZoomed) {
      return Promise.resolve()
    }

    //load high res
    this.isZoomed = true

    //this.mesh.userData.enableBloom = true
    this.#spinSound = sound.startLoop('drag_2')
    this.#savedQuaterinion.copy(this.mesh.quaternion)
    this.#spinQuaternion.copy(this.mesh.quaternion)
    //this.disable()
    return new Promise<void>(resolve => {
      this.#zoomInPosition.copy(this.mesh.position)
      this.#zoomInTween = gsap.to(this.#zoomInPosition, {
        x: position.x,
        y: position.y,
        z: position.z,
        duration: 0.3,
        ease: Sine.easeInOut,
        onComplete: () => {
          //check if zoomOut was triggered before animation complete
          if (this.isZoomed) {
            this.addTouchListener()
          }

          resolve()
        }
      })
    })
  }

  zoomOut(updateCallback: () => void, completeCallback: () => void) {
    if (this.#zoomInTween) {
      this.#zoomInTween.kill()
      this.#zoomInTween = undefined
    }

    this.lastTouchesLength = 0
    this.#spinSound?.fade(this.#spinSound.volume(), 0, 300)
    this.removeTouchListener()

    const to = new Vector3(
      this.body.position.x * MATTER_TO_THREE_SCALE,
      -this.body.position.y * MATTER_TO_THREE_SCALE,
      PhysicsLayerZ.Front
    )

    this.mesh.userData.enableBloom = globalRenderer.allowBloomOnTokens

    gsap.to(this.#zoomInPosition, {
      x: to.x,
      y: to.y,
      z: to.z,
      duration: 0.5,
      ease: Sine.easeOut,
      onUpdate: () => {
        updateCallback()
      },
      onComplete: () => {
        this.#spinSound?.stop()
        completeCallback()

        this.isZoomed = false
      }
    })

    const qTo = this.#savedQuaterinion
    gsap.to(this.mesh.quaternion, {
      x: qTo.x,
      y: qTo.y,
      z: qTo.z,
      w: qTo.w,
      duration: 0.3,
      ease: Sine.easeInOut
    })
  }
}
